import { useContext } from 'react'
import { ModalKey, ModalContext } from 'components/Modals/ModalContext'
import { injuryOptions } from 'functions/injuryUtils'

import ContentFrame from 'components/Frame/ContentFrame'
import { Switch } from '@headlessui/react'
import BrandColorButton from 'components/Buttons/BrandColorButton'

export default function ProgramSimulatorSettings({
    programMetadata = null,
    programLevelScore,
    setProgramLevelScore = () => {},
    selectedProgramLevel,
    kinesisEvents = [],
    setKinesisEvents = () => {},
    volumeDetrainingModifier,
    setVolumeDetrainingModifier = () => {},
    assessmentJSON = null,
    setAssessmentJSON = () => {},
    injuries = [],
    setInjuries = () => {},
    equipmentProfileJSON,
    setEquipmentProfileJSON = () => {},
    workoutConfigWeekdayAssignmentsJSON = [],
    setWorkoutConfigWeekdayAssignmentsJSON = () => {},
    workoutConfigWeekdayAssignmentOverridesJSON = [],
    setWorkoutConfigWeekdayAssignmentOverridesJSON = () => {},
    OnGenerateButton = () => {},
    generateLoading
}) {

    const { ShowModal } = useContext(ModalContext)

    const ShowProgramLevelScoreSelectorModal = () => {
        const modalData = {
            key: ModalKey.ProgramLevelScoreSelector,
            size: 'half',
            programLevels: programMetadata.programLevels,
            programLevelScore: programLevelScore,
            setProgramLevelScore: setProgramLevelScore
        }

        ShowModal(modalData)
    }

    const ShowEventsModal = () => {
        const modalData = {
            key: ModalKey.SimulatorKinesisEvents,
            size: 'third',
            kinesisEvents: kinesisEvents,
            setKinesisEvents: setKinesisEvents
        }

        ShowModal(modalData)
    }

    const ShowAssessmentInputModal = () => {
        const modalData = {
            key: ModalKey.AssessmentInput,
            size: 'workout-summary',
            programMetadata: programMetadata,
            assessmentJSON: assessmentJSON,
            setAssessmentJSON: setAssessmentJSON
        }

        ShowModal(modalData)
    }

    const ShowInjuryInputModal = () => {
        const modalData = {
            key: ModalKey.InjuryInput,
            size: 'quarter',
            injuries: injuries,
            setInjuries: setInjuries
        }

        ShowModal(modalData)
    }

    const ShowEditEquipmentProfileModal = () => {
        const modalData = {
            key: ModalKey.EditEquipmentProfile,
            size: 'third',
            equipmentProfileJSON: equipmentProfileJSON,
            buttonAction: setEquipmentProfileJSON
        }

        ShowModal(modalData)
    }

    const ShowWorkoutConfigWeekdayAssignmentsModal = () => {
        const modalData = {
            key: ModalKey.WorkoutConfigWeekdayAssignments,
            size: 'third',
            workoutConfigs: programMetadata?.workoutConfigs ?? [],
            workoutConfigWeekdayAssignmentsJSON: workoutConfigWeekdayAssignmentsJSON,
            buttonAction: async (workoutConfigWeekdayAssignments) => {
                setWorkoutConfigWeekdayAssignmentsJSON(workoutConfigWeekdayAssignments)
            }
        }

        ShowModal(modalData)
    }

    const ShowWorkoutConfigWeekdayAssignmentOverridesModal = () => {
        const modalData = {
            key: ModalKey.WorkoutConfigWeekdayAssignmentOverrides,
            size: 'third',
            workoutConfigs: programMetadata?.workoutConfigs ?? [],
            workoutConfigWeekdayAssignmentOverridesJSON: workoutConfigWeekdayAssignmentOverridesJSON,
            buttonAction: async (workoutConfigWeekdayAssignmentOverrides) => {
                setWorkoutConfigWeekdayAssignmentOverridesJSON(workoutConfigWeekdayAssignmentOverrides)
            }
        }

        ShowModal(modalData)
    }

    if (programMetadata === null) return null

    const assessmentActive = assessmentJSON !== null
    const activeBaseEquipmentCount = Object.keys(equipmentProfileJSON?.baseEquipment).filter(key => equipmentProfileJSON.baseEquipment[key] === true).length ?? 0
    const baseEquipmentCount = Object.keys(equipmentProfileJSON?.baseEquipment).length ?? 0

    return (
        <ContentFrame>
            <div className='w-full p-4 space-y-4'>
                <div className='grid grid-cols-2'>
                    <div className='flex items-center'>
                        <p className='text-xs flex-1'>Level</p>
                    </div>

                    <div
                        className="w-full flex items-center justify-center px-3 py-2 gap-x-2 border border-gray-300 rounded-md cursor-pointer"
                        onClick={() => ShowProgramLevelScoreSelectorModal()}
                    >
                        <div
                            className='w-2 h-2 rounded-full'
                            style={{ backgroundColor: selectedProgramLevel?.colorHex }}
                        />

                        <p className='text-xs truncate'>
                            {programLevelScore} ({selectedProgramLevel?.name}) 
                        </p>
                    </div>
                </div>

                <div className='grid grid-cols-2'>
                    <div className='flex items-center'>
                        <p className='text-xs flex-1'>Events</p>
                    </div>

                    <div
                        className="w-full flex items-center justify-center px-3 py-2 border border-gray-300 rounded-md cursor-pointer"
                        onClick={() => ShowEventsModal()}
                    >
                        <p className='text-xs'>{kinesisEvents.length} {kinesisEvents.length === 1 ? 'event' : 'events'}</p>
                    </div>
                </div>

                <div className='grid grid-cols-2'>
                    <div className='flex items-center'>
                        <p className='text-xs flex-1'>Volume Detraining</p>
                    </div>

                    <div className='relative'>
                        <input
                            type='number'
                            min={0}
                            max={100}
                            className='w-full px-2 py-2 text-xs border border-gray-300 rounded-md'
                            value={volumeDetrainingModifier}
                            onChange={(e) => setVolumeDetrainingModifier(e.target.value)}
                        />

                        <p className='text-xs text-gray-500 absolute bottom-2 right-3'>
                            weeks
                        </p>
                    </div>
                </div>

                <div className='grid grid-cols-2'>
                    <p className='text-xs flex items-center'>Assessment</p>

                    <div
                        className="flex items-center justify-center py-2 border border-gray-300 rounded-md cursor-pointer"
                        onClick={() => ShowAssessmentInputModal()}
                    >
                        <Switch
                            checked={assessmentActive}
                            className={`
                        relative inline-flex h-4 w-8 flex-shrink-0 rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out
                        ${assessmentActive ? 'bg-orange-600' : 'bg-gray-200'}
                    `}
                        >
                            <span className="sr-only">Use setting</span>
                            <span
                                aria-hidden="true"
                                className={`
                            pointer-events-none inline-block h-3 w-3 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out
                            ${assessmentActive ? 'translate-x-4' : 'translate-x-0'}
                        `}
                            />
                        </Switch>
                    </div>
                </div>

                <div className='grid grid-cols-2'>
                    <div className='flex items-center'>
                        <p className='text-xs flex-1'>Injury</p>
                    </div>

                    <div
                        className="w-full flex items-center justify-center px-3 py-2 border border-gray-300 rounded-md cursor-pointer"
                        onClick={() => ShowInjuryInputModal()}
                    >
                        <p className='text-xs truncate'>{injuries.length === 0 ? 'None' : injuries.map((injury) => injuryOptions.find((option) => option.key === injury)?.label ?? injury).join(', ')}</p>
                    </div>
                </div>

                <div className='grid grid-cols-2'>
                    <div className='flex items-center'>
                        <p className='text-xs flex-1'>Equipment</p>
                    </div>

                    <div
                        className="w-full flex items-center justify-center px-3 py-2 border border-gray-300 rounded-md cursor-pointer"
                        onClick={() => ShowEditEquipmentProfileModal()}
                    >
                        <p className='text-xs'>{activeBaseEquipmentCount} / {baseEquipmentCount} active</p>
                    </div>
                </div>

                <div className='grid grid-cols-2'>
                    <div className='flex items-center'>
                        <p className='text-xs flex-1'>Schedule</p>
                    </div>

                    <div
                        className="w-full flex items-center justify-center px-3 py-2 border border-gray-300 rounded-md cursor-pointer"
                        onClick={() => ShowWorkoutConfigWeekdayAssignmentsModal()}
                    >
                        <p className='text-xs'>{workoutConfigWeekdayAssignmentsJSON?.length ?? 0} workouts / week</p>
                    </div>
                </div>

                <div className='grid grid-cols-2'>
                    <div className='flex items-center'>
                        <p className='text-xs flex-1'>Schedule Overrides</p>
                    </div>

                    <div
                        className="w-full flex items-center justify-center px-3 py-2 border border-gray-300 rounded-md cursor-pointer"
                        onClick={() => ShowWorkoutConfigWeekdayAssignmentOverridesModal()}
                    >
                        <p className='text-xs'>{workoutConfigWeekdayAssignmentOverridesJSON?.length ?? 0} overrides</p>
                    </div>
                </div>

                <BrandColorButton
                    size='extra-small'
                    text='Generate'
                    action={OnGenerateButton()}
                    isLoading={generateLoading}
                />
            </div>
        </ContentFrame>
    )
}